import React, { useState, useRef, useEffect } from 'react';
import ProgressiveImage from './components/ProgressiveImage';
import { Link } from 'react-router-dom';
import { ReviewCards } from './components/ReviewCards';
import { reviews } from './data/reviews';

const App: React.FC = () => {
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const signupFormRef = useRef<HTMLDivElement>(null);
  const getButtonRef = useRef<HTMLButtonElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const validateEmail = (email: string) => {
    console.log('Validating email:', email);
    // Simpler regex that should definitely accept test@closingtheory.com
    const re = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const isValid = re.test(email);
    console.log('Is valid:', isValid);
    return isValid;
  };

  const handleSignup = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      setIsError(true);
      setTimeout(() => setIsError(false), 520);
      return;
    }

    try {
      const response = await fetch('https://66rgf6kk4c.execute-api.us-east-1.amazonaws.com/production/v1/website/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip'
        },
        body: JSON.stringify({ email: trimmedEmail })
      });

      if (!response.ok) {
        throw new Error('Signup failed');
      }

      setIsSuccess(true);
      setTimeout(() => {
        setIsOnWaitlist(true);
        setTimeout(() => {
          setIsSignupOpen(false);
          setIsSuccess(false);
          setEmail('');
        }, 100);
      }, 1500);
    } catch (error) {
      console.error('Signup error:', error);
      setIsError(true);
      setTimeout(() => setIsError(false), 520);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSignup();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // If signup is open and click is outside form and not on get button
      if (isSignupOpen && 
          signupFormRef.current && 
          !signupFormRef.current.contains(event.target as Node) &&
          getButtonRef.current && 
          !getButtonRef.current.contains(event.target as Node)) {
        setIsSignupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSignupOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (getButtonRef.current) {
        const rect = getButtonRef.current.getBoundingClientRect();
        setShowStickyHeader(rect.top < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isSignupOpen && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [isSignupOpen]);

  const handleIconClick = () => {
    if (!isSpinning) {
      setIsSpinning(true);
      setTimeout(() => setIsSpinning(false), 500); // Reset after animation completes
    }
  };

  const handleHeaderGetClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setIsSignupOpen(true);
    }, 500); // Wait for scroll to complete before showing signup
  };

  const headerContentClasses = `header-content ${isSignupOpen ? 'signup-active' : ''}`;

  useEffect(() => {
    // Check if document is already loaded
    if (document.readyState === 'complete') {
      setIsLoading(false);
    } else {
      // If not, wait for load event
      const handleLoad = () => {
        setIsLoading(false);
      };
      window.addEventListener('load', handleLoad);
      
      // Set a backup timeout in case load event doesn't fire
      const timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => {
        window.removeEventListener('load', handleLoad);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <img 
          src={`${process.env.PUBLIC_URL}/images/features/app-icon.png`}
          alt="Loading..."
          className="loading-icon"
        />
      </div>
    );
  }

  return (
    <div className="app">
      <div className={`sticky-header ${showStickyHeader ? 'visible' : ''}`}>
        <div className="sticky-header-content">
          <div className="sticky-left">
            <span>Househunt</span>
          </div>
          <div className="sticky-center">
            <img 
              src={`${process.env.PUBLIC_URL}/images/features/app-icon.png`} 
              alt="Househunt" 
              className="sticky-app-icon" 
            />
          </div>
          <div className="sticky-right">
            <button 
              className={`get-button ${isOnWaitlist ? 'completed' : ''}`} 
              onClick={handleHeaderGetClick}
            >
              {isOnWaitlist ? (
                <span className="button-checkmark" />
              ) : (
                'Get'
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Header Section */}
      <header className="header">
        <div className={headerContentClasses}>
          <div className="app-info">
            <img 
              src={`${process.env.PUBLIC_URL}/images/features/app-icon.png`} 
              className={`app-icon ${isSpinning ? 'spinning' : ''}`}
              onClick={handleIconClick}
              alt="Househunt"
            />
            <div className="app-details">
              <h1>Househunt</h1>
              <p className="subtitle">Monopoly with real houses</p>
            </div>
          </div>
          <div className="signup-container">
            <div className="get-button-container">
              <button 
                ref={getButtonRef}
                className={`get-button ${isSignupOpen ? 'hidden' : ''} ${isOnWaitlist ? 'completed' : ''}`}
                onClick={() => !isOnWaitlist && setIsSignupOpen(true)}
              >
                {isOnWaitlist ? (
                  <span className="button-checkmark" />
                ) : (
                  'Get'
                )}
              </button>
              {isOnWaitlist && (
                <div className="coming-soon-text">
                  <span className="coming-soon-label">Coming Soon</span>
                  <span className="coming-soon-date">Expected Summer 2025</span>
                </div>
              )}
            </div>
            {!isOnWaitlist && (
              <div 
                ref={signupFormRef}
                className={`signup-form ${isSignupOpen ? 'visible' : ''}`}
              >
                <div className="email-input-container">
                  <input
                    ref={emailInputRef}
                    type="email"
                    className="email-input"
                    value={email}
                    onChange={(e) => {
                      console.log('Updated email value:', e.target.value);
                      setEmail(e.target.value);
                    }}
                    placeholder="Enter your email"
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <button
                  className={`signup-button ${isError ? 'error' : ''} ${isSuccess ? 'success' : ''}`}
                  onClick={handleSignup}
                  disabled={isSubmitting || isSuccess}
                >
                  {isError ? 'Invalid' : isSuccess ? <span className="checkmark" /> : 'Sign Up'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Stats Section */}
        <div className="stats">
          <div className="stat-item">
            <span className="stat-header">104 RATINGS</span>
            <span className="rating">4.4</span>
            <img 
              src={`${process.env.PUBLIC_URL}/images/features/stars-count.png`} 
              alt="4.4 out of 5 stars" 
              className="stars-rating"
              loading="lazy" 
            />
          </div>
          <div className="stat-item">
            <span className="stat-header">CATEGORY</span>
            <img 
              src={`${process.env.PUBLIC_URL}/images/features/simulation-icon.png`} 
              alt="" 
              className="category-icon"
              loading="lazy" 
            />
            <span className="stat-label">Simulation</span>
          </div>
          <div className="stat-item">
            <span className="stat-header">LIVE IN</span>
            <img src={`${process.env.PUBLIC_URL}/images/features/canada-icon.png`} alt="" className="location-icon" />
            <span className="stat-label">Canada</span>
          </div>
          <div className="stat-item">
            <span className="stat-header">COMING SOON</span>
            <img src={`${process.env.PUBLIC_URL}/images/features/us-icon.png`} alt="" className="flag-icon" />
            <span className="stat-label">United States</span>
          </div>
          <div className="stat-item">
            <span className="stat-header">DEVELOPER</span>
            <img src={`${process.env.PUBLIC_URL}/images/features/developer-icon.png`} alt="" className="developer-icon" />
            <span className="stat-label">Closing Theory Studios, Inc.</span>
          </div>
        </div>
      </header>

      {/* Features Grid */}
      <main className="features">
        <div className="features-grid">
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/guess-prices.png`}
              alt="Guess house prices"
              priority={true}
            />
          </div>
          
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/buy-houses.png`}
              alt="Buy houses"
              priority={true}
            />
          </div>
          
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/renovate.png`}
              alt="Renovate"
            />
          </div>
          
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/passive-income.png`}
              alt="Passive income"
            />
          </div>
          
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/sell-houses.png`}
              alt="Sell houses"
            />
          </div>
          
          <div className="feature-card">
            <ProgressiveImage
              src={`${process.env.PUBLIC_URL}/images/features/unlock-city.png`}
              alt="Unlock city"
            />
          </div>
        </div>
      </main>

      {/* Reviews Section */}
      <section className="reviews">
        <h2>Ratings & Reviews</h2>
        <div className="rating-summary">
          <div className="review-container review-container-left">
            <div className="rating-big">
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span className="number">4.4</span>
                <span className="out-of">out of 5</span>
              </div>
              <div className="total-ratings">104 Ratings</div>
            </div>
          </div>
          <div className="review-container review-container-right">
            <div className="rating-bars">
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img 
                    src={`${process.env.PUBLIC_URL}/images/features/stars-5.png`} 
                    alt="5 stars" 
                    loading="lazy"
                  />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '69%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-4.png`} alt="4 stars" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '21%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-3.png`} alt="3 stars" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '6%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-2.png`} alt="2 stars" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '1%' }}></div>
                </div>
              </div>
              <div className="rating-bar-row">
                <div className="stars-rating-small">
                  <img src={`${process.env.PUBLIC_URL}/images/features/stars-1.png`} alt="1 star" />
                </div>
                <div className="rating-bar">
                  <div className="bar-fill" style={{ width: '3%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <h3 className="helpful-reviews-header">Most helpful reviews</h3>
        <div className="reviews-scroll">
          {[...reviews]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="review-title">{review.title}</div>
                  <div className="review-meta">
                    <img src={`${process.env.PUBLIC_URL}/images/features/stars-5.png`} alt="5 stars" className="review-stars" />
                    <span className="review-date">{new Date(review.date).toLocaleDateString()}</span>
                    <span className="review-author">{review.author}</span>
                  </div>
                </div>
                <div className="review-body">
                  {review.content}
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Add this before the closing </div> of the app container */}
      <footer className="footer">
        <div className="footer-links">
          <Link to="/support">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default App; 